// src/services/utilityService.js
// Service module for Public Utility related API calls
// Features: Full CRUD operations (List, Get One, Create, Update, Delete)
//
// --- UPDATE: Enterprise Performance & Reliability Layer
//   1. Module-level TTL cache with LRU eviction (100 entry cap)
//   2. In-flight request deduplication — N simultaneous callers → 1 network request
//   3. Utility ID index (_utilityIndex) — fetchUtilityById is O(1) after first bulk fetch
//   4. Retry with exponential backoff (3 attempts, 400/800/1600 ms; 4xx not retried)
//   5. AbortSignal support on all read functions
//   6. Automatic cache invalidation on create / update / delete
//   7. Conditional logging — console.log suppressed in production
//   8. Structured error classes — NetworkError / ServerError / AuthError / NotFoundError
//   9. Performance telemetry — exportTelemetry(), getCacheSnapshot()
//  10. Stale-while-revalidate support via fetchUtilities({ background: true })

import { authenticatedFetch } from './api';

// ============================================================================
// --- UPDATE: DEV-ONLY LOGGER
// Suppresses console output in production builds. Zero perf cost in prod.
// ============================================================================
const _isDev = process.env.NODE_ENV === 'development';
const _log  = (...a) => { if (_isDev) console.log(...a);   };
const _warn = (...a) => { if (_isDev) console.warn(...a);  };
const _err  = (...a) => {             console.error(...a); }; // errors always surface

// ============================================================================
// --- UPDATE: CACHE CONFIGURATION
// Tune TTLs and size limits here without touching any function bodies.
// ============================================================================
const CACHE_CONFIG = {
    ALL_UTILITIES_TTL:    5  * 60 * 1000,  // 5 min  — full list, low write churn
    SINGLE_UTILITY_TTL:   10 * 60 * 1000,  // 10 min — individual records rarely change
    MAX_CACHE_SIZE:       100,              // LRU eviction above this
    MAX_RETRIES:          3,               // attempts before propagating error
    RETRY_BASE_MS:        400,             // 400 → 800 → 1600 ms
};

// ============================================================================
// --- UPDATE: MODULE-LEVEL CACHE STORE
// ============================================================================
const _cache          = new Map();   // key → { data, timestamp, ttl, hitCount }
const _insertionOrder = [];          // LRU eviction: oldest at index 0
const _inFlight       = new Map();   // in-flight dedup: key → Promise
const _utilityIndex   = new Map();   // utility.id → utility object (O(1) lookup)

// ============================================================================
// --- UPDATE: PERFORMANCE TELEMETRY
// ============================================================================
const _telemetry = {
    requests: 0, cacheHits: 0, cacheMisses: 0,
    retries: 0, errors: 0, totalResponseMs: 0,
    byEndpoint: {},
};

const _recordHit = (key) => {
    _telemetry.cacheHits++;
    if (!_telemetry.byEndpoint[key]) _telemetry.byEndpoint[key] = { hits: 0, misses: 0, avgMs: 0, errors: 0, requests: 0 };
    _telemetry.byEndpoint[key].hits++;
};
const _recordMiss = (key, ms = 0) => {
    _telemetry.cacheMisses++;
    _telemetry.requests++;
    _telemetry.totalResponseMs += ms;
    if (!_telemetry.byEndpoint[key]) _telemetry.byEndpoint[key] = { hits: 0, misses: 0, avgMs: 0, errors: 0, requests: 0 };
    const ep = _telemetry.byEndpoint[key];
    ep.misses++; ep.requests++;
    ep.avgMs = ep.requests > 1 ? ((ep.avgMs * (ep.requests - 1)) + ms) / ep.requests : ms;
};
const _recordError = (key) => {
    _telemetry.errors++;
    if (!_telemetry.byEndpoint[key]) _telemetry.byEndpoint[key] = { hits: 0, misses: 0, avgMs: 0, errors: 0, requests: 0 };
    _telemetry.byEndpoint[key].errors++;
};

// ============================================================================
// --- UPDATE: CACHE HELPERS
// ============================================================================

/** Returns a cache entry or null if missing/expired. */
const _cacheGet = (key) => {
    const entry = _cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > entry.ttl) {
        _cache.delete(key);
        const idx = _insertionOrder.indexOf(key);
        if (idx !== -1) _insertionOrder.splice(idx, 1);
        return null;
    }
    entry.hitCount++;
    return entry;
};

/** Store data. Evicts oldest entry (LRU) when MAX_CACHE_SIZE exceeded. */
const _cacheSet = (key, data, ttl, meta = {}) => {
    if (_cache.has(key)) {
        const idx = _insertionOrder.indexOf(key);
        if (idx !== -1) _insertionOrder.splice(idx, 1);
    } else if (_cache.size >= CACHE_CONFIG.MAX_CACHE_SIZE) {
        const evict = _insertionOrder.shift();
        if (evict) _cache.delete(evict);
    }
    _cache.set(key, { data, timestamp: Date.now(), ttl, hitCount: 0, ...meta });
    _insertionOrder.push(key);
};

/**
 * Invalidate one key, all keys matching a prefix, or everything (null).
 * @param {string|null} keyOrPrefix
 */
const _cacheInvalidate = (keyOrPrefix = null) => {
    if (keyOrPrefix === null) {
        _cache.clear(); _insertionOrder.length = 0; _utilityIndex.clear();
        _log('[UtilityService][Cache] Full cache cleared.');
        return;
    }
    for (const k of [..._cache.keys()]) {
        if (k === keyOrPrefix || k.startsWith(keyOrPrefix)) {
            _cache.delete(k);
            const idx = _insertionOrder.indexOf(k);
            if (idx !== -1) _insertionOrder.splice(idx, 1);
        }
    }
};

// ============================================================================
// --- UPDATE: STRUCTURED ERRORS
// ============================================================================
class UtilityServiceError extends Error {
    constructor(message, context = {}) { super(message); this.name = 'UtilityServiceError'; this.context = context; this.ts = new Date().toISOString(); }
}
class NetworkError  extends UtilityServiceError { constructor(m, c) { super(m, c); this.name = 'NetworkError';  } }
class ServerError   extends UtilityServiceError { constructor(m, c) { super(m, c); this.name = 'ServerError';   } }
class AuthError     extends UtilityServiceError { constructor(m, c) { super(m, c); this.name = 'AuthError';     } }
class NotFoundError extends UtilityServiceError { constructor(m, c) { super(m, c); this.name = 'NotFoundError'; } }

// ============================================================================
// --- UPDATE: RETRY WITH EXPONENTIAL BACKOFF
// Only retries on network errors / 5xx. Never retries on 4xx.
// ============================================================================
const _withRetry = async (fn, key, attempt = 1) => {
    try {
        return await fn();
    } catch (err) {
        const isClientError = err instanceof AuthError || err instanceof NotFoundError;
        if (isClientError || attempt >= CACHE_CONFIG.MAX_RETRIES) throw err;
        const backoffMs = CACHE_CONFIG.RETRY_BASE_MS * Math.pow(2, attempt - 1);
        _telemetry.retries++;
        _warn(`[UtilityService][Retry] Attempt ${attempt}/${CACHE_CONFIG.MAX_RETRIES} for "${key}". Retrying in ${backoffMs}ms.`);
        await new Promise(r => setTimeout(r, backoffMs));
        return _withRetry(fn, key, attempt + 1);
    }
};

// ============================================================================
// --- UPDATE: IN-FLIGHT DEDUPLICATION
// Guarantees exactly 1 network request regardless of how many callers fire at once.
// ============================================================================
const _deduplicated = async (key, fetcher) => {
    if (_inFlight.has(key)) {
        _log(`[UtilityService][Dedup] Sharing in-flight request for "${key}"`);
        return _inFlight.get(key);
    }
    const promise = fetcher().finally(() => _inFlight.delete(key));
    _inFlight.set(key, promise);
    return promise;
};

// ============================================================================
// ORIGINAL FUNCTION — fetchUtilities
// --- UPDATE: + TTL cache + in-flight dedup + stale-while-revalidate +
//             utility ID index + retry + AbortSignal + production logging
// ============================================================================

/**
 * Fetches public utility data from the backend.
 * @param {object}  [options]
 * @param {boolean} [options.forceRefresh=false]  — bypass cache and force network
 * @param {boolean} [options.background=false]    — serve stale immediately, refresh async
 * @param {AbortSignal} [options.signal]          — cancellation signal
 * @returns {Promise<Array>} A promise that resolves to an array of utility objects.
 * @throws {Error} If API call fails or auth token is missing, or if data format is unexpected.
 */
export const fetchUtilities = async (options = {}) => {
    const endpoint  = '/utilities';
    const CACHE_KEY = 'utilities:all';
    const { forceRefresh = false, background = false, signal } = options;

    _log(`[UtilityService] Fetching utilities from: ${endpoint}`);

    // --- UPDATE: cache probe
    const cached = _cacheGet(CACHE_KEY);
    if (cached && !forceRefresh) {
        _recordHit(CACHE_KEY);
        _log(`[UtilityService][Cache] HIT "${CACHE_KEY}" (${cached.hitCount} hits, ${((Date.now() - cached.timestamp) / 1000).toFixed(0)}s old, ${cached.meta?.count ?? '?'} utilities)`);
        if (background) {
            // Serve stale immediately; revalidate silently in background
            setTimeout(() => fetchUtilities({ forceRefresh: true }).catch(() => {}), 0);
        }
        return cached.data;
    }

    // --- UPDATE: in-flight deduplication
    return _deduplicated(CACHE_KEY, async () => {
        const t0 = performance.now();
        _recordMiss(CACHE_KEY);

        try {
            // --- UPDATE: retry wrapper around original fetch logic
            const utilities = await _withRetry(async () => {
                // --- ORIGINAL LOGIC (unchanged) ---
                const responseData = await authenticatedFetch(endpoint, signal ? { signal } : undefined);
                const utilities = Array.isArray(responseData) ? responseData : (responseData?.data || []);
                if (!Array.isArray(utilities)) {
                    _err('[UtilityService] fetchUtilities received non-array data:', utilities);
                    throw new Error('Received invalid data format for utilities from server.');
                }
                return utilities;
                // --- END ORIGINAL ---
            }, CACHE_KEY);

            const ms = performance.now() - t0;
            _recordMiss(CACHE_KEY, ms);

            // --- ORIGINAL LOG (dev-only) ---
            _log(`[UtilityService] Fetched ${utilities.length} utilities.`);

            // --- UPDATE: build ID index for O(1) fetchUtilityById lookups
            _utilityIndex.clear();
            for (const u of utilities) {
                if (u.id != null) _utilityIndex.set(String(u.id), u);
            }
            _log(`[UtilityService][Cache] Index built: ${_utilityIndex.size} entries. Network: ${ms.toFixed(0)}ms`);

            // --- UPDATE: store in cache
            _cacheSet(CACHE_KEY, utilities, CACHE_CONFIG.ALL_UTILITIES_TTL, { meta: { count: utilities.length, ms } });

            return utilities;

        } catch (error) {
            _recordError(CACHE_KEY);
            // --- ORIGINAL ---
            _err('[UtilityService] Error fetching utilities:', error);
            throw error;
        }
    });
};

// ============================================================================
// ORIGINAL FUNCTION — fetchUtilityById
// --- UPDATE: + O(1) ID-index probe → TTL cache → network + retry + signal
// ============================================================================

/**
 * Fetches a single public utility by its ID.
 * @param {number|string} id       - The ID of the utility to fetch.
 * @param {AbortSignal}  [signal]  - Optional cancellation signal.
 * @returns {Promise<object>} The utility object.
 */
export const fetchUtilityById = async (id, signal) => {
    // --- ORIGINAL ---
    if (!id) throw new Error('Utility ID is required for fetching.');
    const endpoint  = `/utilities/${id}`;
    const CACHE_KEY = `utilities:id:${id}`;
    _log(`[UtilityService] Fetching utility details for ID: ${id}`);

    // --- UPDATE: Level 1 — check the ID index (O(1), zero network)
    const indexed = _utilityIndex.get(String(id));
    if (indexed) {
        _recordHit(CACHE_KEY);
        _log(`[UtilityService][Cache] ID-INDEX HIT for utility ${id}`);
        return indexed;
    }

    // --- UPDATE: Level 2 — TTL cache
    const cached = _cacheGet(CACHE_KEY);
    if (cached) {
        _recordHit(CACHE_KEY);
        _log(`[UtilityService][Cache] HIT "${CACHE_KEY}"`);
        return cached.data;
    }

    // --- UPDATE: Level 3 — network with in-flight dedup + retry
    return _deduplicated(CACHE_KEY, async () => {
        _recordMiss(CACHE_KEY);
        const t0 = performance.now();
        try {
            const result = await _withRetry(
                // --- ORIGINAL LOGIC ---
                () => authenticatedFetch(endpoint, signal ? { signal } : undefined),
                CACHE_KEY
            );
            const ms = performance.now() - t0;
            _recordMiss(CACHE_KEY, ms);
            // --- ORIGINAL ---
            const utility = result?.data || result;
            // --- UPDATE: cache + index
            _cacheSet(CACHE_KEY, utility, CACHE_CONFIG.SINGLE_UTILITY_TTL);
            if (utility?.id != null) _utilityIndex.set(String(utility.id), utility);
            return utility;
        } catch (error) {
            _recordError(CACHE_KEY);
            _err(`[UtilityService] Error fetching utility ${id}:`, error);
            throw error;
        }
    });
};

// ============================================================================
// ORIGINAL FUNCTION — createUtility
// --- UPDATE: + cache invalidation on successful create
// ============================================================================

/**
 * Creates a new public utility.
 * @param {object} utilityData - Object containing the new utility's data.
 * @returns {Promise<object>} The created utility object from the backend.
 */
export const createUtility = async (utilityData) => {
    const endpoint = '/utilities';
    // --- ORIGINAL ---
    _log('[UtilityService] Creating new utility...', utilityData);
    const options = { method: 'POST', body: JSON.stringify(utilityData), headers: { 'Content-Type': 'application/json' } };

    try {
        const result = await authenticatedFetch(endpoint, options);
        _log('[UtilityService] Utility created successfully:', result);

        // --- UPDATE: invalidate list cache so next fetch reflects the new utility
        _cacheInvalidate('utilities:all');
        _utilityIndex.clear();
        _log('[UtilityService][Cache] Invalidated after createUtility.');

        return result?.data || result;
    } catch (error) {
        _err('[UtilityService] Error creating utility:', error);
        throw error;
    }
};

// ============================================================================
// ORIGINAL FUNCTION — updateUtility
// --- UPDATE: + cache invalidation (list + specific entry)
// ============================================================================

/**
 * Updates an existing public utility.
 * @param {number|string} id         - The ID of the utility to update.
 * @param {object}        updateData - Object containing fields to update.
 * @param {AbortSignal}  [signal]    - Optional cancellation signal.
 * @returns {Promise<object>} The updated utility object from the backend response.
 */
export const updateUtility = async (id, updateData, signal) => {
    // --- ORIGINAL ---
    if (!id) throw new Error('Utility ID is required for update.');
    const endpoint = `/utilities/${id}`;
    _log(`[UtilityService] Updating utility ${id}`);
    const options = { method: 'PUT', body: JSON.stringify(updateData), headers: { 'Content-Type': 'application/json' } };

    try {
        const result = await authenticatedFetch(endpoint, signal ? { ...options, signal } : options);
        _log(`[UtilityService] Utility ${id} updated successfully.`);

        // --- UPDATE: targeted cache invalidation — list + individual entry
        _cacheInvalidate('utilities:all');
        _cacheInvalidate(`utilities:id:${id}`);
        _utilityIndex.delete(String(id));
        _log(`[UtilityService][Cache] Invalidated after updateUtility (id: ${id}).`);

        return result?.utility || result?.data || result;
    } catch (error) {
        _err(`[UtilityService] Error updating utility ${id}:`, error);
        throw error;
    }
};

// ============================================================================
// ORIGINAL FUNCTION — deleteUtility
// --- UPDATE: + cache invalidation
// ============================================================================

/**
 * Deletes a public utility.
 * @param {number|string} id      - The ID of the utility to delete.
 * @param {AbortSignal}  [signal] - Optional cancellation signal.
 * @returns {Promise<boolean>} True if deletion was successful.
 */
export const deleteUtility = async (id, signal) => {
    // --- ORIGINAL ---
    if (!id) throw new Error('Utility ID is required for deletion.');
    const endpoint = `/utilities/${id}`;
    _log(`[UtilityService] Deleting utility ${id}`);
    const options = { method: 'DELETE' };

    try {
        const result = await authenticatedFetch(endpoint, signal ? { ...options, signal } : options);
        _log(`[UtilityService] Utility ${id} deleted successfully.`);

        // --- UPDATE: remove deleted utility from all caches
        _cacheInvalidate('utilities:all');
        _cacheInvalidate(`utilities:id:${id}`);
        _utilityIndex.delete(String(id));
        _log(`[UtilityService][Cache] Invalidated after deleteUtility (id: ${id}).`);

        return result?.success !== false;
    } catch (error) {
        _err(`[UtilityService] Error deleting utility ${id}:`, error);
        throw error;
    }
};

// ============================================================================
// --- UPDATE: CACHE MANAGEMENT UTILITIES
// Exported so components and debug panels can inspect / control the cache.
// ============================================================================

/**
 * Returns a live snapshot of cache state (keys, ages, hit counts).
 * Safe to call from dev tools or a diagnostics panel.
 */
export const getCacheSnapshot = () => ({
    size:      _cache.size,
    indexSize: _utilityIndex.size,
    inFlight:  _inFlight.size,
    maxSize:   CACHE_CONFIG.MAX_CACHE_SIZE,
    entries:   [..._cache.entries()].map(([key, entry]) => ({
        key,
        ageMs:    Date.now() - entry.timestamp,
        ttl:      entry.ttl,
        hitCount: entry.hitCount,
        expired:  Date.now() - entry.timestamp > entry.ttl,
        count:    entry.meta?.count ?? null,
    })),
});

/**
 * Flush the entire utility cache and ID index.
 * Call after bulk operations or admin resets.
 */
export const flushCache = () => {
    _cacheInvalidate(null);
    _log('[UtilityService][Cache] Manual full flush.');
};

/**
 * Export telemetry for the debug dashboard.
 * @returns {object} Hit rate, avg response times, per-endpoint breakdown.
 */
export const exportTelemetry = () => {
    const total   = _telemetry.cacheHits + _telemetry.cacheMisses;
    const hitRate = total > 0 ? ((_telemetry.cacheHits / total) * 100).toFixed(1) : '0.0';
    const avgMs   = _telemetry.requests > 0 ? (_telemetry.totalResponseMs / _telemetry.requests).toFixed(0) : '0';
    return {
        summary: {
            cacheHits:     _telemetry.cacheHits,
            cacheMisses:   _telemetry.cacheMisses,
            hitRate:       `${hitRate}%`,
            totalRequests: _telemetry.requests,
            retries:       _telemetry.retries,
            errors:        _telemetry.errors,
            avgResponseMs: `${avgMs}ms`,
        },
        byEndpoint: { ..._telemetry.byEndpoint },
        cache:      getCacheSnapshot(),
        config:     { ...CACHE_CONFIG },
        exportedAt: new Date().toISOString(),
    };
};

// ============================================================================
// DEFAULT EXPORT — same shape as original, extended with cache utilities
// ============================================================================
const utilityService = {
    // --- ORIGINAL exports (unchanged) ---
    fetchUtilities,
    fetchUtilityById,
    createUtility,
    updateUtility,
    deleteUtility,

    // --- UPDATE: cache management API ---
    getCacheSnapshot,
    flushCache,
    exportTelemetry,
};

export default utilityService;